import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx';

interface CellProps {
  before?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  after?: ReactNode;
  chevron?: boolean;
  onClick?: () => void;
  destructive?: boolean;
  /** Accent-colored title, e.g. "+ Додати витрату". */
  accent?: boolean;
  multilineTitle?: boolean;
  /** Let a long subtitle wrap instead of being cut. */
  multilineSubtitle?: boolean;
  /** `top` lines the leading visual and the trailing value up with the title (tall rows). */
  align?: 'center' | 'top';
  className?: string;
}

/** One row of a Section: optional leading visual, title/subtitle, trailing value or control. */
export function Cell({
  before,
  title,
  subtitle,
  after,
  chevron,
  onClick,
  destructive,
  accent,
  multilineTitle,
  multilineSubtitle,
  align = 'center',
  className,
}: CellProps) {
  const top = align === 'top';
  const content = (
    <>
      {before && <div className={cx('flex shrink-0 items-center', top && 'pt-3')}>{before}</div>}
      <div className="min-w-0 flex-1 py-3">
        <div
          className={cx(
            'text-[17px] leading-snug',
            !multilineTitle && 'truncate',
            destructive && 'text-destructive',
            accent && 'text-accent',
          )}
        >
          {title}
        </div>
        {subtitle && (
          <div
            className={cx(
              'mt-0.5 text-[15px] text-subtitle',
              multilineSubtitle ? 'leading-snug' : 'truncate',
            )}
          >
            {subtitle}
          </div>
        )}
      </div>
      {after !== undefined && (
        <div
          className={cx('flex shrink-0 items-center gap-1 text-[17px] text-hint', top && 'pt-3')}
        >
          {after}
        </div>
      )}
      {chevron && <ChevronRight className="size-5 shrink-0 text-hint" aria-hidden />}
    </>
  );

  const classes = cx(
    'cell relative flex min-h-12 w-full gap-3 px-4 text-left',
    top ? 'items-start' : 'items-center',
    onClick && 'active:bg-black/5 dark:active:bg-white/5',
    className,
  );
  return onClick ? (
    <button type="button" className={classes} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className={classes}>{content}</div>
  );
}
